<script>
import { mapActions, mapGetters } from 'vuex';
import MarkingsAssignList from '@/components/markings/assign-list/MarkingsAssignList.vue';

export default {
  name: 'CockpitMatryoshkaItemOperations',
  props: {
    labelArchiveId: Number,
  },
  data: () => ({
    showAssignModal: false,
    pendingToAssign: false,
    loadingToAssignItems: false,
    toAssignItemsCurrentPage: 1,
    toAssignItemsPerPage: 10,
    toAssignItemsTotal: 0,
    toAssignItems: [],
    labelArchiveDetails: null,
    pending: false,
    innerPending: false,
    showPalletCloseModal: false,
    closingPallet: false,
    calculatedPalletAmount: 0,
    calculatedPalletAmountFromQueue: 0,
    closePalletItemsAmount: 0,
    partialPalletEditing: false,
  }),
  components: { MarkingsAssignList },
  emits: [
    'operation-success',
  ],
  computed: {
    ...mapGetters([
      'globalSettings',
    ]),
    ...mapGetters('packingSettings', [
      'getUnitById',
      'getHigherLevelUnit',
      'getLowerLevelUnit',
      'getUnitToUnitRelation',
    ]),
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
      'emptyAmount',
    ]),
    lowerLevelUnit() {
      if (!this.labelArchiveDetails) return null;
      return this.getLowerLevelUnit(
        this.labelArchiveDetails.unitId,
        this.labelArchiveDetails.skuId,
      );
    },
    lowerLowerLevelUnit() {
      if (!this.lowerLevelUnit) return null;
      return this.getLowerLevelUnit(
        this.lowerLevelUnit.id,
        this.labelArchiveDetails.skuId,
      );
    },
    higherLevelUnit() {
      if (!this.labelArchiveDetails) return null;
      return this.getHigherLevelUnit(
        this.labelArchiveDetails.unitId,
        this.labelArchiveDetails.skuId,
      );
    },
    hasParent() {
      if (!this.labelArchiveDetails) return false;
      return (this.labelArchiveDetails?.containers || []).find(
        container => container.containerUnitId === this.higherLevelUnit?.id,
      );
    },
    isMiddleUnit() {
      return Boolean(this.lowerLevelUnit && this.higherLevelUnit);
    },
    isLastUnit() {
      return Boolean(this.lowerLevelUnit && !this.higherLevelUnit);
    },
    isFirstUnit() {
      return Boolean(!this.lowerLevelUnit && this.higherLevelUnit);
    },
    currentParent() {
      if (!this.labelArchiveDetails) return null;
      return this.labelArchiveDetails.containers.find(
        container => container.containerUnitId === this.higherLevelUnit?.id,
      );
    },
    lowerChildren() {
      if (!this.lowerLevelUnit) return [];
      return this.labelArchiveDetails.containedLabels.filter(
        label => label.unitId === this.lowerLevelUnit.id,
      );
    },
    emptyLowerChildrenAmount() {
      if (!this.lowerLevelUnit) return 0;
      return this.emptyAmount(this.lowerChildren.map(l => l.id));
    },
    lowerUnitChildrenAmount() {
      if (!this.lowerLevelUnit) return 0;
      return this.lowerChildren.length - this.emptyLowerChildrenAmount;
    },

    amountOfChildrenInsideLowerUnit() {
      if (!this.lowerLevelUnit || !this.lowerLowerLevelUnit) return 0;
      return this.getUnitToUnitRelation(
        this.lowerLowerLevelUnit?.id,
        this.lowerLevelUnit?.id,
        this.labelArchiveDetails?.skuId,
      )?.amount || 0;
    },
    lowestUnitChildrenAmount() {
      return this.lowerUnitChildrenAmount * this.amountOfChildrenInsideLowerUnit;
    },
    maxLowestUnitChildrenAmount() {
      return this.maxAmount * this.amountOfChildrenInsideLowerUnit;
    },
    closingFullPallet() {
      return this.maxLowestUnitChildrenAmount === this.calculatedPalletAmount
        && this.maxLowestUnitChildrenAmount === this.calculatedPalletAmountFromQueue;
    },
    maxAmount() {
      if (!this.lowerLevelUnit) return 0;
      return this.getUnitToUnitRelation(
        this.lowerLevelUnit.id,
        this.labelArchiveDetails.unitId,
        this.labelArchiveDetails.skuId,
      )?.amount || 0;
    },
    isFull() {
      if (!this.lowerLevelUnit) return false;
      return this.lowerUnitChildrenAmount >= this.maxAmount;
    },
    isInAggregation() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.labelStatus === 'Active';
    },
    isUtilized() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.labelStatus === 'Utilized';
    },
    isQualityChecked() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.labelStatus === 'QualityCheck';
    },
    isStorageSample() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.labelStatus === 'StorageSample';
    },
    disabledItemIds() {
      return [this.currentParent?.containerId || 0];
    },
    isInAggregationQueue() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.scanIndex;
    },
    isOpenPallet() {
      if (!this.labelArchiveDetails) return false;
      return this.labelArchiveDetails.labelStatus !== 'Active' && this.isLastUnit;
    },
    availableOperations() {
      const operations = [];

      if (this.isOpenPallet) {
        operations.push({
          key: 'closePallet',
          icon: 'fas fa-box-open',
          label: 'Zamknij paletę',
          subLabel: `Zamknięcie palety spowoduje,
          że nie będzie można do niej dodać ani przełączyć elementów podrzędnych.
          Zostanie oznaczona jako zamknięta.`,
          action: this.openPalletCloseModal,
        });
      }

      if (this.higherLevelUnit && this.isFirstUnit && this.hasParent && (!this.isMiddleUnit || !this.hasParent)) {
        operations.push({
          key: 'detachWithAggregation',
          icon: 'fas fa-external-link-alt',
          label: 'Przełóż do innego elementu zbiorczego',
          subLabel: this.isInAggregation
            ? 'Zmiana elementu zbiorczego na inny element zbiorczy'
            : '',
          action: this.detachWithAggregation,
        });
      }

      if (this.isInAggregation
      && !this.isLastUnit
      && (!this.isMiddleUnit || !this.hasParent)
      && this.isInAggregationQueue) {
        operations.push({
          key: 'detachFromAggregation',
          icon: 'fas fa-align-slash',
          label: 'Usuń z kolejki agregacji',
          subLabel: this.isInAggregation
            ? 'Element zostanie usunięty z kolejki agregacji i nie będzie brany pod uwagę w przyszłej agregacji'
            : '',
          action: this.detachFromAggregation,
        });
      }

      if (!this.isUtilized && !this.isLastUnit && (!this.isMiddleUnit || !this.hasParent)) {
        operations.push({
          key: 'utilize',
          icon: 'fas fa-trash',
          label: 'Zutyliuj',
          subLabel: this.isInAggregation
            ? `Element zostanie oznaczony jako zutylizowany,
            zostanie usunięty z kolejki agregacji i nie będzie brany pod uwagę w przyszłej agregacji`
            : 'Element zostanie oznaczony jako zutylizowany i nie będzie brany pod uwagę przy agregacji',
          action: this.utilize,
        });
      }

      if (!this.isQualityChecked && !this.isLastUnit && (!this.isMiddleUnit || !this.hasParent)) {
        operations.push({
          key: 'qualityCheck',
          icon: 'fas fa-flask-gear',
          label: 'Weź do kontroli jakości',
          subLabel: this.isInAggregation
            ? `Element zostanie oznaczony jako zabrany do kontroli jakości,
            zostanie usunięty z kolejki agregacji i nie będzie brany pod uwagę w przyszłej agregacji`
            : 'Element zostanie oznaczony jako zabrany do kontroli jakości i nie będzie brany pod uwagę przy agregacji',
          action: this.qualityCheck,
        });
      }

      if (!this.isStorageSample && !this.isLastUnit && (!this.isMiddleUnit || !this.hasParent)) {
        operations.push({
          key: 'storageSample',
          icon: 'fas fa-warehouse',
          label: 'Weź próbkę do magazynu',
          subLabel: this.isInAggregation
            ? `Element zostanie oznaczony jako w próbka w magazynie,
            zostanie usunięty z kolejki agregacji i nie będzie brany pod uwagę w przyszłej agregacji`
            : 'Element zostanie oznaczony jako w próbka w magazynie i nie będzie brany pod uwagę przy agregacji',
          action: this.storageSample,
        });
      }

      return operations;
    },
  },
  watch: {
    closePalletItemsAmount(v) {
      if (v < 0) {
        this.closePalletItemsAmount = 0;
      }
    },
    calculatedPalletAmount(value) {
      this.closePalletItemsAmount = value;
    },
    showPalletCloseModal() {
      this.partialPalletEditing = false;
      this.closePalletItemsAmount = this.calculatedPalletAmount;
    },
    toAssignItemsCurrentPage() {
      this.fetchToAssignItems();
    },
  },
  methods: {
    ...mapActions('labelArchive', [
      'getLabelArchive',
      'detachLabelArchiveItem',
      'detachLabelArchiveWithAggregation',
      'attachLabelArchiveItem',
      'utilizeLabelArchive',
      'qualityCheckLabelArchive',
      'storageSampleLabelArchive',
      'getLabelArchiveByIds',
      'closePalletLabelArchive',
      'getLabelArchiveItem',
      'getLabelArchiveByContainerLabel',
    ]),
    async confirm() {
      const value = await this.$bvModal.msgBoxConfirm('Jesteś pewny?', {
        size: 'sm',
        buttonSize: 'md',
        okVariant: 'success',
        okTitle: 'Tak',
        cancelTitle: 'Nie',
        centered: true,
      });

      return value;
    },
    async confirmClosePalletWithDifferentAmountThanFromQueue(
      amount,
      amountFromQueue,
    ) {
      const value = await this.$bvModal.msgBoxConfirm(
        `Czy na pewno chcesz zamknąć paletę z liczbą sztuk wynoszącą ${amount},
        różniącą się od tej wynikającej z kolejki agregacji (${amountFromQueue})?`,
        {
          size: 'sm',
          buttonSize: 'md',
          okVariant: 'success',
          okTitle: 'Tak',
          cancelTitle: 'Nie',
          centered: true,
        },
      );

      return value;
    },
    calculatePalletAmount(children) {
      return children.reduce((acc, curr) => {
        const splittedScanCode = (curr?.scanCode || '').toString().split(';');
        const amount = Number(splittedScanCode?.[splittedScanCode.length - 1] || '0');
        return acc + amount;
      }, 0);
    },
    calculatePalletAmountFromQueue(children) {
      return children.reduce((acc, curr) => {
        const amount = (curr?.containedLabels || []).filter(child => !this.isLabelArchiveEmpty(child.id))?.length || 0;
        return acc + amount;
      }, 0);
    },
    async detachWithAggregation() {
      this.showAssignModal = true;
      await this.fetchToAssignItems();
    },
    async fetchToAssignItems() {
      if (!this.labelArchiveDetails) return;
      if (!this.showAssignModal) return;

      const { plantCode } = this.globalSettings;
      this.loadingToAssignItems = true;

      const unitId = this.getHigherLevelUnit(
        this.labelArchiveDetails.unitId,
        this.labelArchiveDetails.skuId,
      )?.id;

      const { data } = await this.getLabelArchive({
        params: {
          plantCode,
          query: {
            orderId: this.labelArchiveDetails.orderId,
            skuId: this.labelArchiveDetails.skuId,
            unitId,
            page_number: this.toAssignItemsCurrentPage,
            page_size: this.toAssignItemsPerPage,
            labelStatus: 'Active',
          },
        },
      });

      this.toAssignItems = data.items;
      this.toAssignItemsTotal = data?.totalCount || 0;
      this.loadingToAssignItems = false;
    },
    async openPalletCloseModal() {
      this.showPalletCloseModal = true;
    },
    async closePallet() {
      if (
        Number(this.closePalletItemsAmount) !== Number(this.calculatedPalletAmountFromQueue)
      ) {
        if (!await this.confirmClosePalletWithDifferentAmountThanFromQueue(
          this.closePalletItemsAmount,
          this.calculatedPalletAmountFromQueue,
        )) return;
      }
      this.innerPending = true;
      this.closingPallet = true;
      const partPalletQuantity = this.closePalletItemsAmount === this.maxLowestUnitChildrenAmount
        ? undefined
        : this.closePalletItemsAmount;
      await this.closePalletLabelArchive({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
          query: {
            partPalletQuantity,
          },
        },
      });
      this.$emit('operation-success');
      this.innerPending = false;
      this.closingPallet = false;
    },
    async utilize() {
      if (!await this.confirm()) return;

      this.innerPending = true;
      await this.utilizeLabelArchive({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
      });
      this.$emit('operation-success');
      this.innerPending = false;
    },
    async qualityCheck() {
      if (!await this.confirm()) return;

      this.innerPending = true;
      await this.qualityCheckLabelArchive({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
      });
      this.$emit('operation-success');
      this.innerPending = false;
    },
    async storageSample() {
      if (!await this.confirm()) return;

      this.innerPending = true;
      await this.storageSampleLabelArchive({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
      });
      this.$emit('operation-success');
      this.innerPending = false;
    },
    async detachFromAggregation() {
      if (!await this.confirm()) return;

      this.innerPending = true;
      await this.detachLabelArchiveWithAggregation({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
        data: {
          destId: this.currentParent?.containerId || undefined,
        },
      });
      this.$emit('operation-success');
      this.innerPending = false;
    },
    async handleSelectConnectItem(id) {
      if (!await this.confirm()) return;

      this.pendingToAssign = true;
      await this.detachLabelArchiveItem({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
        data: {
          destId: this.currentParent?.containerId || undefined,
        },
      });
      await this.attachLabelArchiveItem({
        params: {
          plantCode: this.globalSettings.plantCode,
          id: this.labelArchiveId,
        },
        data: {
          destId: id,
        },
      });
      this.showAssignModal = false;
      this.$emit('operation-success');
      this.pendingToAssign = false;
    },
  },
  async mounted() {
    this.pending = true;
    const { data } = await this.getLabelArchiveItem({
      params: {
        plantCode: this.globalSettings.plantCode,
        id: this.labelArchiveId,
      },
    });
    this.labelArchiveDetails = data || null;
    this.calculatedPalletAmount = 0;

    try {
      const { data: children } = await this.getLabelArchiveByContainerLabel({
        params: {
          plantCode: this.globalSettings.plantCode,
          query: {
            id: this.labelArchiveId,
          },
        },
      });
      if (children?.length) {
        this.calculatedPalletAmount = this.calculatePalletAmount(children);
        this.calculatedPalletAmountFromQueue = this.calculatePalletAmountFromQueue(children);
      }
      // eslint-disable-next-line no-empty
    } catch {}

    this.pending = false;
  },
};
</script>

<template>
  <div>
    <Loader v-if="pending" />
    <BOverlay
      v-else
      :show="innerPending"
    >
      <div class="operations-list">
        <div
          v-for="operation in availableOperations"
          :key="operation.key"
          class="operation-item"
          @click.stop.prevent="operation.action"
        >
          <div class="d-flex align-items-center">
            <i
              :class="operation.icon"
              class="mr-4 fa-fw"
            />
            <div>
              {{ operation.label }}

              <p class="ml-auto small text-muted mb-0">
                {{ operation.subLabel }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="availableOperations.length === 0"
          class="text-center mt-4 mx-auto text-muted"
        >
          <div class="d-flex align-items-center">
            <i class="fas fa-info-circle mr-4 fa-fw" />

            Brak dostępnych operacji
          </div>
        </div>
      </div>
    </BOverlay>

    <BModal
      v-model="showAssignModal"
      hide-footer
      :title="$t('cockpit.selectItemToConnect')"
      size="xl"
    >
      <BOverlay :show="pendingToAssign">
        <div class="to-assign-list-wrapper">
          <Loader
            v-if="loadingToAssignItems"
          />

          <MarkingsAssignList
            v-else
            :lower-level="false"
            :list="toAssignItems"
            :disabled-ids="disabledItemIds"
            @select-item="handleSelectConnectItem"
          />
        </div>

        <div class="to-assign-pagination-box">
          <BPagination
            v-model="toAssignItemsCurrentPage"
            :per-page="toAssignItemsPerPage"
            :total-rows="toAssignItemsTotal"
            size="sm"
            pills
            limit="5"
            first-number
            last-number
          />
        </div>
      </BOverlay>
    </BModal>

    <BModal
      v-model="showPalletCloseModal"
      hide-footer
      title="Zamknij paletę"
    >
      <BOverlay :show="closingPallet">
        <div>
          <div>
            <div class="d-flex align-items-center mb-2">
              <label for="itemsAmount">
                Potwierdź ilość sztuk na palecie:
              </label>

              <BButton
                v-if="!partialPalletEditing"
                variant="primary"
                class="btn-sm ml-auto"
                @click="partialPalletEditing = !partialPalletEditing"
              >
                Edytuj ilość
              </BButton>
            </div>

            <input
              id="itemsAmount"
              v-model="closePalletItemsAmount"
              type="number"
              :disabled="!partialPalletEditing"
              class="form-control"
            >
            <div
              v-if="partialPalletEditing"
              class="button-group d-flex justify-content-center mt-4"
              style="gap: 10px;"
            >
              <BButton
                variant="primary"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount += 1"
              >
                +1
              </BButton>
              <BButton
                variant="primary"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount += 5"
              >
                +5
              </BButton>
              <BButton
                variant="primary"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount += 10"
              >
                +10
              </BButton>
              <BButton
                variant="danger"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount -= 10"
              >
                -10
              </BButton>
              <BButton
                variant="danger"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount -= 5"
              >
                -5
              </BButton>
              <BButton
                variant="danger"
                class="btn-sm"
                style="width: 70px;"
                @click="closePalletItemsAmount -= 1"
              >
                -1
              </BButton>
            </div>
          </div>
        </div>

        <div v-if="false">
          <p>
            <span class="font-weight-bold">
              Wpisana ilość:
            </span>
            {{ closePalletItemsAmount }}
          </p><p>
            <span class="font-weight-bold">
              Wyliczona ilość sztuk na palecie z kolejki:
            </span>
            {{ calculatedPalletAmountFromQueue }}
          </p>
          <p class="mb-0">
            <span class="font-weight-bold">
              Wyliczona ilość sztuk na palecie z etykiet:
            </span>
            {{ calculatedPalletAmount }}
          </p>
        </div>

        <hr class="my-4">

        <div
          class="d-flex justify-content-end mt-4"
          style="gap: 10px;"
        >
          <BButton
            variant="secondary"
            @click="showPalletCloseModal = false"
          >
            Anuluj
          </BButton>
          <BButton
            variant="primary"
            @click="closePallet"
          >
            Zamknij paletę
          </BButton>
        </div>
      </BOverlay>
    </BModal>
  </div>
</template>

<style scoped lang="scss">
.operations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .operation-item {
    border: 1px solid #F0F1F3;
    width: 100%;
    height: 100px;
    padding: 0 20px;
    font-size: 15px;
    font-weight: 500;
    display: flex;
    align-items: center;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      background-color: #F0F1F3;
    }
  }
}

.to-assign-list-wrapper {
  height: calc(100vh - 290px);
  overflow-y: auto;
  margin: 1rem 0;
}

.to-assign-pagination-box {
  display: flex;
  justify-content: center;
  border-top: 1px solid #ECECEC;
  padding-top: 1rem;
}
</style>
